import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { t, tx } from '../../i18n';
import { fmtDate, fmtKB, fmtTime, initials, matchesName, monthName, monthShort, plural, SEARCH_FROM } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { Preview } from '../belege/BelegSheet';
import { Loading } from '../belege/Pages';
import { useNotifications } from '../belege/shared';
import { Sheet } from '../belege/Sheet';
import { employeeArts, requestState } from '../anfragen/labels';
import { RequestForm } from '../anfragen/RequestForm';
import type { RequestsResponse } from '../anfragen/types';
import { useClientApp } from '../context';
import { LOHN_KIND_LABEL, LOHN_KINDS, lohnMonthPath, lohnPath, newEmployeePath } from './labels';
import { VertragForm } from './Arbeitsvertrag';
import { KuendigungForm } from './Kuendigung';
import { LinkSheetHost, WaitingList } from './Links';
import { ChangeForm, TerminationForm } from './Meldungen';
import { useLohnPage } from './nav';
import { VerdienstView } from './Verdienst';
import type { EmployeeDetail, EmployeeItem, FirmResponse, LohnDoc, LohnFirm, MonthResponse } from './types';
import { ConfirmDialog } from '../../components/Dialog';
import { ShareFile } from '../../components/ShareFile';

// Bereich Mitarbeiter: Firma wählen → Mitarbeiter und Lohnunterlagen der Firma → Lohnunterlagen eines Monats.
// Personalakte (?ma=<id>), wartender Link (?link=<id>), Lohnunterlage (?datei=<id>) und angeforderte Unterlage
// (?anfrage=<id>) öffnen sich als Fenster.

// Mitarbeiter · Für welche Firma? (nur mit mehreren Firmen)
export function FirmsPage() {
  const { clients } = useClientApp();
  const one = clients.length === 1 ? clients[0]!.id : null;
  const res = useApi<{ firms: LohnFirm[] }>(one ? null : '/api/app/lohn', { live: ['lohn'] });
  useLohnPage({ title: t('Mitarbeiter') });
  if (one) return <Navigate to={lohnPath(one)} replace />;
  return (
    <>
      <div className="hello">
        <h2>{t('Mitarbeiter')}</h2>
        <p>{t('Für welche Firma?')}</p>
      </div>
      {!res.data ? (
        <Loading error={res.error} />
      ) : (
        <div className="list">
          {res.data.firms.map((f) => (
            <Link key={f.clientId} className="item" to={lohnPath(f.clientId)}>
              <span className="ico" aria-hidden="true">
                {f.name.charAt(0)}
              </span>
              <span className="tx">
                <b>{f.name}</b>
                <small>
                  {t('{n} aktiv', { n: f.aktiv })}
                  {f.abgemeldet ? ` · ${t('{n} abgemeldet', { n: f.abgemeldet })}` : ''}
                  {f.neu ? ` · ${t('{n} neu in Prüfung', { n: f.neu })}` : ''}
                </small>
              </span>
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

// <Firma> · Lohnunterlagen und Mitarbeiter
export function FirmPage({ clientId }: { clientId: string }) {
  const { clientName } = useClientApp();
  const res = useApi<FirmResponse>(`/api/app/lohn/${clientId}`, { live: ['lohn'], clientId });
  const d = res.data;
  const name = d?.client.name ?? clientName(clientId);
  useLohnPage({ title: name, clientId, clientName: name });
  const [query, setQuery] = useState('');
  // viele Mitarbeiter: Listen eingeklappt (Sam 12.10.), beim Suchen offen
  const [openActive, setOpenActive] = useState(false);
  const [openGone, setOpenGone] = useState(false);
  // Abgemeldet (oder letzter Arbeitstag vorbei) steht gleich getrennt unter den aktiven; die Suche filtert beide
  const out = (e: EmployeeItem) => e.abgemeldet || e.gone;
  const all = d?.employees ?? [];
  const hits = all.filter((e) => matchesName(e.name, query));
  const active = hits.filter((e) => !out(e));
  const gone = hits.filter(out);
  const searching = !!query.trim();
  const folds = (n: number) => n >= SEARCH_FROM && !searching;
  // eingeklappt sichtbar bleibt, was noch bei der Kanzlei liegt (neu, Änderung oder Kündigung gemeldet)
  const pending = (e: EmployeeItem) => e.status === 'neu' || e.openChanges > 0 || e.termination?.status === 'offen';
  const visible = (list: EmployeeItem[], open: boolean) => (folds(list.length) && !open ? list.filter(pending) : list);
  const shownActive = visible(active, openActive);
  const shownGone = visible(gone, openGone);

  return (
    <>
      <div className="hello">
        <h2>{name}</h2>
        <p>{t('Lohnunterlagen und Mitarbeiter')}</p>
      </div>
      {!d ? (
        <Loading error={res.error} />
      ) : (
        <>
          {d.links.length > 0 && (
            <>
              <div className="sec">
                <h3>{t('Warten auf Angaben ({n})', { n: d.links.length })}</h3>
              </div>
              <WaitingList links={d.links} />
            </>
          )}
          {all.length >= SEARCH_FROM && (
            <input
              type="search"
              className="find"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('Mitarbeiter suchen')}
              aria-label={t('Mitarbeiter suchen')}
            />
          )}
          <div className="sec">
            <h3>{t('Aktive Mitarbeiter ({n})', { n: active.length })}</h3>
            <Link className="btn2 dark" to={newEmployeePath(clientId)}>
              + {t('Mitarbeiter anlegen')}
            </Link>
          </div>
          {folds(active.length) && <Fold open={openActive} count={active.length} onToggle={() => setOpenActive(!openActive)} />}
          {active.length ? (
            shownActive.length > 0 && <EmployeeList items={shownActive} />
          ) : (
            <div className="files">
              <div className="empty lempty">
                {all.length && query.trim()
                  ? t('Kein Mitarbeiter gefunden.')
                  : t('Noch keine Mitarbeiter. Legen Sie neue Mitarbeiter hier an, Ihre Kanzlei meldet sie dann an.')}
              </div>
            </div>
          )}
          {gone.length > 0 && (
            <>
              <div className="sec">
                <h3>{t('Abgemeldete Mitarbeiter ({n})', { n: gone.length })}</h3>
              </div>
              {folds(gone.length) && <Fold open={openGone} count={gone.length} onToggle={() => setOpenGone(!openGone)} />}
              {shownGone.length > 0 && <EmployeeList items={shownGone} />}
            </>
          )}
          <div className="sec">
            <h3>{t('Lohnunterlagen')}</h3>
          </div>
          {d.months.length ? (
            <div className="list">
              {d.months.map((m) => (
                <Link key={`${m.year}-${m.month}`} className="item" to={lohnMonthPath(clientId, m.year, m.month)}>
                  <span className="ico" aria-hidden="true">
                    {monthShort(m.month)}
                  </span>
                  <span className="tx">
                    <b>
                      {monthName(m.month)} {m.year}
                    </b>
                    <small>{m.kinds.map((k) => t(LOHN_KIND_LABEL[k])).join(', ')}</small>
                  </span>
                  {m.freigabe ? (
                    <span className="badge b-warn">{t('Bitte freigeben')}</span>
                  ) : (
                    <span className="badge b-ok">{plural(m.count, 'Datei', 'Dateien')}</span>
                  )}
                  <span className="chev" aria-hidden="true">
                    ›
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="files">
              <div className="empty lempty">
                {t('Noch keine Lohnunterlagen. Ihre Kanzlei lädt sie nach jeder Abrechnung hier hoch.')}
              </div>
            </div>
          )}
          <LinkSheetHost links={d.links} firm={name} />
        </>
      )}
    </>
  );
}

// Knopf zum Auf- und Zuklappen einer langen Mitarbeiterliste
function Fold({ open, count, onToggle }: { open: boolean; count: number; onToggle(): void }) {
  return (
    <button type="button" className="btn2 fold" aria-expanded={open} onClick={onToggle}>
      {open ? `${t('Zuklappen')} ▴` : `${t('Alle {n} anzeigen', { n: count })} ▾`}
    </button>
  );
}

function EmployeeList({ items }: { items: EmployeeItem[] }) {
  const navigate = useNavigate();
  return (
    <div className="list">
      {items.map((e) => (
        <button
          key={e.id}
          type="button"
          className="item"
          onClick={() => navigate({ search: `?ma=${e.id}` }, { state: { sheet: true } })}
        >
          <span className="ico" aria-hidden="true">
            {initials(e.name)}
          </span>
          <span className="tx">
            <b>{e.name}</b>
            <small>
              {[e.job, e.kind && t(e.kind)].filter(Boolean).join(' · ')}
              {e.abgemeldet && ` · ${t('bis {date}', { date: fmtDate(e.termination!.lastDay) })}`}
            </small>
          </span>
          {e.status === 'neu' ? (
            <span className="badge b-warn">{t('Neu · in Prüfung')}</span>
          ) : e.abgemeldet ? (
            <span className="badge b-none">{t('abgemeldet')}</span>
          ) : e.termination ? (
            <span className="badge b-warn">
              {e.gone
                ? t('Austritt {date}', { date: fmtDate(e.termination.lastDay) })
                : t('Austritt am {date}', { date: fmtDate(e.termination.lastDay) })}
            </span>
          ) : e.openChanges ? (
            <span className="badge b-warn">{t('Änderung gemeldet')}</span>
          ) : null}
          <span className="chev" aria-hidden="true">
            ›
          </span>
        </button>
      ))}
    </div>
  );
}

// Lohn <Monat> <Jahr>: Unterlagen der Kanzlei, nach Art gruppiert
export function MonthPage({ clientId, year, month }: { clientId: string; year: number; month: number }) {
  const navigate = useNavigate();
  const notes = useNotifications();
  const reloadNotes = notes.reload;
  const res = useApi<MonthResponse>(`/api/app/lohn/${clientId}/${year}/${month}`, { live: ['lohn'], clientId });
  const d = res.data;
  useLohnPage({
    title: t('Lohn {month}', { month: monthName(month) }),
    clientId,
    clientName: d?.client.name,
    sub: t('Lohn {month} {year}', { month: monthName(month), year }),
  });

  // Die Meldung „Neue Lohnunterlagen“ dieses Monats gilt als gelesen, sobald der Monat offen ist
  const unread = (notes.data?.items ?? []).some(
    (n) => n.type === 'lohn_unterlagen' && !n.read && n.clientId === clientId && n.link?.year === year && n.link.month === month,
  );
  useEffect(() => {
    if (unread) {
      api
        .post(`/api/app/lohn/${clientId}/${year}/${month}/read`)
        .then(() => reloadNotes())
        .catch(() => {});
    }
  }, [unread, clientId, year, month, reloadNotes]);

  return (
    <>
      <div className="hello">
        <h2>{t('Lohn {month} {year}', { month: monthName(month), year })}</h2>
        <p>{d ? `${d.client.name} · ${t('hochgeladen von Ihrer Kanzlei')}` : ''}</p>
      </div>
      {!d ? (
        <Loading error={res.error} />
      ) : d.docs.length ? (
        LOHN_KINDS.map((k) => {
          const docs = d.docs.filter((x) => x.kind === k.key);
          if (!docs.length) return null;
          return (
            <div className="files lfiles" key={k.key}>
              <h3>
                {t(k.label)} ({docs.length})
              </h3>
              {docs.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  className="file"
                  onClick={() => navigate({ search: `?datei=${x.id}` }, { state: { sheet: true } })}
                >
                  <span>
                    {x.name}
                    <small className="fsub">{t('am {date}', { date: fmtDate(x.createdAt) })}</small>
                    {x.korrektur && <small className="fsub">{korrekturText(x.korrektur)}</small>}
                    {x.freigabe !== undefined && <small className="fsub">{freigabeText(x.freigabe)}</small>}
                  </span>
                  <span className={x.freigabe === null ? 'wait' : 'ok'}>
                    {x.freigabe === null ? t('Prüfen') : t('Ansehen')} <span className="chev">›</span>
                  </span>
                </button>
              ))}
            </div>
          );
        })
      ) : (
        <div className="files">
          <div className="empty lempty">
            {t('Für {month} {year} gibt es keine Lohnunterlagen.', { month: monthName(month), year })}
          </div>
        </div>
      )}
      {d && <DocSheetHost docs={d.docs} reload={res.reload} />}
    </>
  );
}

// Lohnsteuer-Anmeldung zur Freigabe: wartet noch oder wann und von wem freigegeben
function freigabeText(f: LohnDoc['freigabe']) {
  return f
    ? t('Freigegeben von {name} am {date} um {time} Uhr', { name: f.name, date: fmtDate(f.at), time: fmtTime(f.at) })
    : t('Wartet auf Ihre Freigabe');
}

// Ältere Lohnabrechnung, deren Seiten eine Korrektur der Kanzlei ersetzt: wessen Seiten fehlen
function korrekturText(k: NonNullable<LohnDoc['korrektur']>) {
  const date = fmtDate(k.at);
  return k.names.length <= 3
    ? t('Ohne {names} (korrigiert am {date})', { names: k.names.join(', '), date })
    : t('Ohne {n} Mitarbeiter (korrigiert am {date})', { n: k.names.length, date });
}

// Fenster schließen: selbst geöffnet zurück im Verlauf (so schließt auch die Zurück-Geste), sonst nur die Adresse kürzen
export function useCloseSheet(param: string) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  return () => {
    if ((location.state as { sheet?: boolean } | null)?.sheet) navigate(-1);
    else {
      const next = new URLSearchParams(params);
      next.delete(param);
      setParams(next, { replace: true });
    }
  };
}

function DocSheetHost({ docs, reload }: { docs: LohnDoc[]; reload(): Promise<unknown> }) {
  const [params] = useSearchParams();
  const close = useCloseSheet('datei');
  const id = params.get('datei');
  if (!id) return null;
  const doc = docs.find((x) => x.id === id);
  const url = `/api/app/lohn-doc/${id}/file`;
  return (
    <Sheet title={doc?.name ?? t('Lohnunterlage')} onClose={close}>
      {doc ? (
        <>
          {doc.freigabe && (
            <p className="lst-done" role="status">
              ✓ {freigabeText(doc.freigabe)}
            </p>
          )}
          <Preview url={url} mime={doc.mime} name={doc.name} />
          <div className="meta">
            {t(LOHN_KIND_LABEL[doc.kind])} · {fmtKB(doc.size)} · {t('hochgeladen am {date}', { date: fmtDate(doc.createdAt) })}
            {doc.korrektur && (
              <>
                <br />
                {korrekturText(doc.korrektur)}
              </>
            )}
          </div>
          {doc.freigabe === null && <Freigeben id={doc.id} reload={reload} />}
          <div className="row">
            <a className="btn2" href={`${url}?download=1`} download>
              {t('Herunterladen')}
            </a>
            <ShareFile url={`${url}?download=1`} name={doc.name} />
          </div>
        </>
      ) : (
        <p className="meta">{t('Diese Datei gibt es nicht mehr.')}</p>
      )}
    </Sheet>
  );
}

// Lohnsteuer-Anmeldung freigeben (Gesetzes-Prüfung C3): Die Kanzlei übermittelt sie erst danach an das Finanzamt
function Freigeben({ id, reload }: { id: string; reload(): Promise<unknown> }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function release() {
    setBusy(true);
    try {
      await api.post(`/api/app/lohn-doc/${id}/freigeben`);
      await reload();
      toast(t('Freigegeben. Ihre Kanzlei übermittelt die Anmeldung jetzt an das Finanzamt.'));
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
    }
  }
  return (
    <div className="lst-ask" role="status">
      <b>{t('Bitte prüfen und freigeben')}</b>
      <span>
        {t(
          'Ihre Kanzlei übermittelt die Lohnsteuer-Anmeldung erst nach Ihrer Freigabe an das Finanzamt. Stimmt etwas nicht, schreiben Sie Ihrer Kanzlei im Chat.',
        )}
      </span>
      <div className="row">
        <button type="button" className="btn2 dark" disabled={busy} onClick={release}>
          {busy ? t('Einen Moment …') : t('Geprüft, freigeben')}
        </button>
      </div>
    </div>
  );
}

// Personalakte als Fenster über der Firmenseite
export function EmployeeSheetHost() {
  const [params] = useSearchParams();
  const close = useCloseSheet('ma');
  const id = params.get('ma');
  return id ? <EmployeeSheet key={id} id={id} onClose={close} /> : null;
}

// Status-Zeile wie in der Demo: neu, angemeldet, gekündigt (letzter Arbeitstag) und abgemeldet
function statusText(e: EmployeeDetail): string {
  if (e.status === 'neu') return t('Neu · Ihre Kanzlei prüft');
  const term = e.termination;
  if (!term) return t('Angemeldet');
  const last = fmtDate(term.lastDay);
  if (term.status === 'erledigt') return t('Abgemeldet · von Ihrer Kanzlei zum {date}', { date: last });
  return term.gone
    ? t('Ausgeschieden · Kündigung gemeldet, letzter Arbeitstag {date}', { date: last })
    : t('Angemeldet · Kündigung gemeldet, letzter Arbeitstag {date}', { date: last });
}

// Angaben in der Personalakte, deren Wert aus einer festen Auswahl kommt (Deutsch gespeichert): Wert übersetzen
const CHOICE_LABELS = new Set([
  'Geschlecht',
  'Familienstand',
  'Ausweisart',
  'Beschäftigungsart',
  'Weitere Beschäftigung bei einem anderen Arbeitgeber?',
  'Befreiung von der Rentenversicherung',
  'Höchster Schulabschluss',
  'Höchste Berufsausbildung',
  'Konfession',
  'Noch keine Sozialversicherungsnummer',
  'Schwerbehinderung',
]);

function EmployeeSheet({ id, onClose }: { id: string; onClose(): void }) {
  const res = useApi<EmployeeDetail>(`/api/app/employee/${id}`, { live: ['lohn'] });
  const [mode, setMode] = useState<'akte' | 'aenderung' | 'kuendigung' | 'schreiben' | 'anfordern' | 'vertrag' | 'verdienst'>(
    'akte',
  );
  const [storno, setStorno] = useState(false);
  const [delAusweis, setDelAusweis] = useState(false);
  const [delEmployee, setDelEmployee] = useState(false);
  const e = res.data;
  // Wechsel zwischen Akte und Formular: oben anfangen, im Formular gleich das erste Feld
  useEffect(() => {
    document.querySelector('.mapp .sheet')?.scrollTo({ top: 0 });
    if (mode !== 'akte') {
      const first = {
        aenderung: 'g-what',
        kuendigung: 'g-by',
        schreiben: 'g-art',
        anfordern: 'g-art',
        vertrag: 'g-art',
        verdienst: 'vd-monat',
      };
      document.getElementById(first[mode])?.focus();
    }
  }, [mode]);
  const back = () => setMode('akte');
  const sent = () => {
    res.reload();
    setMode('akte');
  };
  const title =
    mode === 'akte'
      ? (e?.name ?? t('Mitarbeiter'))
      : {
          aenderung: t('Änderung melden'),
          kuendigung: t('Kündigung melden'),
          schreiben: t('Kündigung erstellen'),
          anfordern: t('Unterlagen anfordern'),
          vertrag: t('Arbeitsvertrag erstellen'),
          verdienst: t('Verdienstabrechnung'),
        }[mode];

  if (!e) {
    return (
      <Sheet title={title} onClose={onClose}>
        {res.error ? <p className="meta">{res.error}</p> : <div className="spinner" aria-label={t('Lädt')} />}
      </Sheet>
    );
  }
  if (mode !== 'akte') {
    return (
      <Sheet title={title} onClose={onClose}>
        <p className="meta">
          {e.name} · {e.clientName}
        </p>
        {mode === 'aenderung' ? (
          <ChangeForm e={e} onDone={sent} onCancel={back} />
        ) : mode === 'kuendigung' ? (
          <TerminationForm e={e} onDone={sent} onCancel={back} />
        ) : mode === 'schreiben' ? (
          <KuendigungForm e={e} onDone={sent} onCancel={back} />
        ) : mode === 'vertrag' ? (
          <VertragForm e={e} onDone={back} onCancel={back} />
        ) : mode === 'verdienst' ? (
          <VerdienstView e={e} onBack={back} />
        ) : (
          <RequestForm
            arts={employeeArts(e.termination)}
            url={`/api/app/employee/${e.id}/anfragen`}
            placeholder={t('z. B. letzte 3 Monate, für das Jobcenter')}
            hint={t(
              'Die Anfrage geht an Ihre Lohnbuchhaltung. Den Stand sehen Sie hier in der Personalakte und auf der Übersicht unter „Sonstige Unterlagen“.',
            )}
            onDone={back}
            onCancel={back}
          />
        )}
      </Sheet>
    );
  }

  const term = e.termination;
  const open = e.changes.filter((c) => c.status === 'offen');
  const canMeld = e.status === 'aktiv' && !term?.gone;
  // Arbeitsvertrag für neue und angemeldete Mitarbeiter, solange keine Kündigung da ist
  const canVertrag = !term;
  // Wieder einstellen, wenn er ausgeschieden oder abgemeldet ist (einmal je Personalakte)
  const canRehire = !!term && (term.gone || term.status === 'erledigt') && !e.rehiredAs;
  return (
    <Sheet title={title} onClose={onClose}>
      {/* Knöpfe oben: die Personalakte ist lang. Unterlagen gibt es auch für Mitarbeiter, die schon weg sind. */}
      {(e.status === 'aktiv' || canVertrag) && (
        <div className="bwa-row ma-actions">
          {e.status === 'aktiv' && (
            <button type="button" className="btn2 dark" onClick={() => setMode('verdienst')}>
              {t('Verdienstabrechnung herunterladen')}
            </button>
          )}
          {e.status === 'aktiv' && (
            <button type="button" className="btn2" onClick={() => setMode('anfordern')}>
              {t('Unterlagen anfordern')}
            </button>
          )}
          {canMeld && (
            <button type="button" className="btn2" onClick={() => setMode('aenderung')}>
              {t('Änderung melden')}
            </button>
          )}
          {canVertrag && (
            <button type="button" className={e.status === 'neu' ? 'btn2 dark' : 'btn2'} onClick={() => setMode('vertrag')}>
              {t('Arbeitsvertrag erstellen')}
            </button>
          )}
          {canMeld && term?.status !== 'erledigt' && (
            <button type="button" className="btn2" onClick={() => setMode('schreiben')}>
              {t('Kündigung erstellen')}
            </button>
          )}
          {canMeld && !term && (
            <button type="button" className="btn2" onClick={() => setMode('kuendigung')}>
              {t('Kündigung melden')}
            </button>
          )}
          {/* nur solange die Kanzlei noch nicht abgemeldet hat, danach zieht sie die Kündigung zurück (Sam 12.10.) */}
          {term && term.status !== 'erledigt' && !e.rehiredAs && (
            <button type="button" className="btn2" onClick={() => setStorno(true)}>
              {t('Kündigung stornieren')}
            </button>
          )}
          {canRehire && (
            <Link className="btn2" to={`${newEmployeePath(e.clientId)}?wieder=${e.id}`}>
              {t('Wieder einstellen')}
            </Link>
          )}
        </div>
      )}
      {storno && term && (
        <StornoConfirm
          e={e}
          onDone={() => {
            setStorno(false);
            res.reload();
          }}
          onCancel={() => setStorno(false)}
        />
      )}
      <dl className="akte">
        <div>
          <dt>{t('Status')}</dt>
          <dd>
            {statusText(e)}
            {e.status !== 'neu' && !term && e.registeredAt && <small>{t('am {date}', { date: fmtDate(e.registeredAt) })}</small>}
            {term?.status === 'erledigt' && !e.rehiredAs && (
              <small>{t('Zurückziehen kann die Kündigung jetzt nur noch Ihre Kanzlei.')}</small>
            )}
            {e.rehiredFrom?.lastDay && (
              <small>{t('Wiedereinstellung · vorher beschäftigt bis {date}', { date: fmtDate(e.rehiredFrom.lastDay) })}</small>
            )}
            {e.rehiredAs && <small>{t('Wieder eingestellt zum {date}', { date: fmtDate(e.rehiredAs.entryDate) })}</small>}
          </dd>
        </div>
        {term && (
          <div>
            <dt>{t('Kündigung')}</dt>
            <dd>
              {t(term.by)}
              {term.receivedOn && ` · ${t('zugegangen am {date}', { date: fmtDate(term.receivedOn) })}`}
              {term.vacationDays !== null &&
                ` · ${
                  term.vacationDays === 1
                    ? t('Resturlaub {n} Tag', { n: String(term.vacationDays).replace('.', ',') })
                    : t('Resturlaub {n} Tage', { n: String(term.vacationDays).replace('.', ',') })
                }`}
              {term.reason && (
                <small>
                  {term.lastDayOld
                    ? t('Letzter Arbeitstag von Ihrer Kanzlei korrigiert (vorher {date})', { date: fmtDate(term.lastDayOld) })
                    : t('Von Ihrer Kanzlei korrigiert')}{' '}
                  · {t('Grund: {reason}', { reason: term.reason })}
                </small>
              )}
            </dd>
          </div>
        )}
        {open.map((c) => (
          <div key={c.id}>
            <dt>{t('Änderung {what}', { what: t(c.what) })}</dt>
            <dd>
              {c.value}
              {c.validFrom && ` · ${t('ab {date}', { date: fmtDate(c.validFrom) })}`}
              <small>{t('gemeldet, Ihre Kanzlei bearbeitet')}</small>
            </dd>
          </div>
        ))}
        {e.info.map((r) => (
          <div key={r.label}>
            <dt>{t(r.label)}</dt>
            <dd>
              {CHOICE_LABELS.has(r.label) ? t(r.value) : r.value}
              {r.note && <small>{tx(r.note)}</small>}
            </dd>
          </div>
        ))}
        {e.files.map((f) => (
          <div key={f.kind}>
            <dt>{t(f.label)}</dt>
            <dd>✓ {t('hochgeladen')}</dd>
          </div>
        ))}
        <AusweisRow e={e} onDelete={() => setDelAusweis(true)} />
      </dl>
      <AusweisDelete e={e} open={delAusweis} onClose={() => setDelAusweis(false)} onDone={res.reload} />
      {e.status === 'aktiv' && <EmployeeRequests clientId={e.clientId} employeeId={e.id} />}
      {e.status === 'neu' ? (
        <>
          <div className="src">
            {t('Ihre Kanzlei prüft die Angaben und meldet den Mitarbeiter bei Krankenkasse und Finanzamt an.')}
          </div>
          <p className="ma-delete">
            <button type="button" className="linkbtn" onClick={() => setDelEmployee(true)}>
              {t('Mitarbeiter löschen')}
            </button>
          </p>
          <EmployeeDelete e={e} open={delEmployee} onClose={() => setDelEmployee(false)} onDone={onClose} />
        </>
      ) : term?.status === 'offen' ? (
        <div className="src">
          {t('Ihre Kanzlei meldet den Mitarbeiter zum letzten Arbeitstag ab und erstellt die letzte Lohnabrechnung.')}
        </div>
      ) : term ? (
        <div className="src">
          {t('Ihre Kanzlei hat den Mitarbeiter abgemeldet. Er steht jetzt unter „Abgemeldete Mitarbeiter“.')}
          {!term.gone && ` ${t('Bis zum letzten Arbeitstag können Sie noch Änderungen melden.')}`}
        </div>
      ) : null}
    </Sheet>
  );
}

// Ausweiskopie (Sam 12.10.): nur mit Einverständnis gespeichert; widerruft der Mitarbeiter, löscht der Mandant sie hier.
// Ohne Kopie (kein Einverständnis) der Hinweis, den Ausweis persönlich anzusehen.
function AusweisRow({ e, onDelete }: { e: EmployeeDetail; onDelete(): void }) {
  const copy = e.files.some((f) => f.kind.startsWith('ausweis'));
  if (!copy && !e.info.some((r) => r.label === 'Ausweisart')) return null;
  return (
    <div>
      <dt>{t('Ausweiskopie')}</dt>
      {copy ? (
        <dd>
          {e.ausweisConsent
            ? t('gespeichert mit Einverständnis vom {date}', { date: fmtDate(e.ausweisConsent.at) })
            : t('gespeichert, Einverständnis nicht erfasst')}
          <small>
            <button type="button" className="linkbtn" onClick={onDelete}>
              {t('Ausweiskopie löschen')}
            </button>
          </small>
        </dd>
      ) : (
        <dd>
          {t('keine gespeichert')}
          <small>{t('Bitte lassen Sie sich den Ausweis persönlich zeigen.')}</small>
        </dd>
      )}
    </div>
  );
}

// Rückfrage zum Löschen (außerhalb der Liste, sonst gelten deren Zeilen-Stile im Fenster)
function AusweisDelete({ e, open, onClose, onDone }: { e: EmployeeDetail; open: boolean; onClose(): void; onDone(): void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    try {
      await api.del(`/api/app/employee/${e.id}/ausweis`);
      toast(t('Ausweiskopie gelöscht'));
      onClose();
      onDone();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <ConfirmDialog
      open={open}
      title={t('Ausweiskopie löschen?')}
      text={t(
        'Zum Beispiel, wenn {name} das Einverständnis widerruft. Die Kopie wird endgültig gelöscht, die übrigen Angaben bleiben.',
        { name: e.name },
      )}
      confirmLabel={t('Ja, löschen')}
      danger
      busy={busy}
      onConfirm={remove}
      onCancel={onClose}
    />
  );
}

// Mitarbeiter löschen, solange die Kanzlei ihn noch nicht angemeldet hat (DSGVO, Prüfung 12.10.): z. B. doppelt angelegt
// oder er fängt doch nicht an. Angemeldete löscht die App nach Ablauf der Aufbewahrungsfrist.
function EmployeeDelete({ e, open, onClose, onDone }: { e: EmployeeDetail; open: boolean; onClose(): void; onDone(): void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    try {
      await api.del(`/api/app/employee/${e.id}`);
      toast(t('{name} gelöscht', { name: e.name }));
      onClose();
      onDone();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <ConfirmDialog
      open={open}
      title={t('{name} löschen?', { name: e.name })}
      text={t(
        'Zum Beispiel, wenn der Mitarbeiter doppelt angelegt ist oder doch nicht anfängt. Die Angaben und alle hochgeladenen Dateien werden endgültig gelöscht.',
      )}
      confirmLabel={t('Ja, löschen')}
      danger
      busy={busy}
      onConfirm={remove}
      onCancel={onClose}
    />
  );
}

// Gemeldete Kündigung zurücknehmen, solange die Kanzlei sie noch nicht bearbeitet hat (Sam 12.10.)
function StornoConfirm({ e, onDone, onCancel }: { e: EmployeeDetail; onDone(): void; onCancel(): void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.del(`/api/app/employee/${e.id}/termination`);
      toast(t('Kündigung storniert · {name} bleibt angemeldet', { name: e.name }));
      onDone();
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
    }
  };
  return (
    <div className="confirm" role="alertdialog" aria-label={t('Kündigung stornieren')}>
      {t('Kündigung von {name} wirklich stornieren? {name} bleibt angemeldet, Ihre Lohnbuchhaltung bekommt eine Meldung.', {
        name: e.name,
      })}
      {e.termination?.file && ` ${t('Das hochgeladene Kündigungsschreiben wird gelöscht.')}`}
      <div className="row">
        <button type="button" className="btn2 dark" onClick={go} disabled={busy} autoFocus>
          {t('Ja, stornieren')}
        </button>
        <button type="button" className="btn2" onClick={onCancel} disabled={busy}>
          {t('Abbrechen')}
        </button>
      </div>
    </div>
  );
}

// Angeforderte Unterlagen zu diesem Mitarbeiter mit Stand; ein Klick öffnet die Anfrage (Dateien, Kommentare)
function EmployeeRequests({ clientId, employeeId }: { clientId: string; employeeId: string }) {
  const navigate = useNavigate();
  const res = useApi<RequestsResponse>(`/api/app/anfragen/${clientId}`, { live: ['anfragen', 'notifications'], clientId });
  const list = res.data?.requests.filter((r) => r.employee?.id === employeeId) ?? [];
  if (!list.length) return null;
  return (
    <div className="files rq-emp">
      <h3>{t('Angeforderte Unterlagen ({n})', { n: list.length })}</h3>
      {list.map((r) => {
        const s = requestState(r);
        return (
          <button
            key={r.id}
            type="button"
            className="file"
            onClick={() => navigate({ search: `?anfrage=${r.id}` }, { state: { sheet: true } })}
          >
            <span>
              {tx(r.label)}
              <small className="fsub">
                {r.note ? `„${r.note}“ · ` : ''}
                {t('angefordert am {date}', { date: fmtDate(r.createdAt) })}
              </small>
            </span>
            <span className={s.tone === 'ok' ? 'ok' : 'wait'}>
              {r.fresh && !r.ask ? `${t('Neu')} · ${t(s.label)}` : t(s.label)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
